/*
  LYTES purchase options (one-time vs Auto Delivery), for blocks/lytes-purchase-options.liquid.

  - Keeps each product's choice (mode + plan) in memory, so it survives Horizon re-rendering
    the product section when the shopper changes variant.
  - Updates the option prices from the block's JSON when the variant changes.
  - Removes Seal Subscriptions' own widget from the product section, so only one set of
    purchase options shows. Seal still manages the subscriptions.
  - Keeps a selling_plan input in the add-to-cart form in step with the choice at all times.
    (Adding it at submit is too late: Horizon's own submit handler reads the form first.)
    The submit listener below only re-checks it.
  - Clicking anywhere on an option card selects that option.
*/
(() => {
  if (window.__lytesPurchaseOptions) return;
  window.__lytesPurchaseOptions = true;

  /** @type {Map<string, {mode: string, plan: string}>} */
  const choices = new Map();

  const money = (cents, format) => {
    const amount = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (format || '${{amount}}')
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(cents / 100)))
      .replace(/\{\{\s*amount\s*\}\}/, amount)
      .replace(/<[^>]*>/g, '');
  };

  const readData = (po) => {
    const el = po.querySelector('[data-lytes-po-data]');
    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      return null;
    }
  };

  const sectionOf = (el) => el.closest('.shopify-section') || document;

  const formFor = (po) => sectionOf(po).querySelector('form[data-type="add-to-cart-form"]');

  /** Apply the remembered choice to freshly rendered markup */
  const restore = (po) => {
    const saved = choices.get(po.dataset.productId);
    if (!saved) return;
    const radio = po.querySelector(`input[data-lytes-po-mode="${saved.mode}"]`);
    if (radio) radio.checked = true;
    if (saved.plan) {
      po.querySelectorAll('[data-lytes-po-freqs] button').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.plan === saved.plan));
      });
    }
  };

  const currentPlan = (po, data) => {
    const active = po.querySelector('[data-lytes-po-freqs] button[aria-pressed="true"]');
    return active ? active.dataset.plan : data.defaultPlan;
  };

  const update = (po) => {
    const data = readData(po);
    if (!data) return;
    const auto = po.querySelector('input[data-lytes-po-mode="auto"]');
    const isAuto = Boolean(auto && auto.checked);
    const plan = currentPlan(po, data);

    po.dataset.lytesPlan = isAuto && plan ? plan : '';
    choices.set(po.dataset.productId, { mode: isAuto ? 'auto' : 'one', plan });

    po.querySelectorAll('.lytes-po__opt').forEach((opt) => {
      const radio = opt.querySelector('.lytes-po__radio');
      opt.classList.toggle('is-active', Boolean(radio && radio.checked));
    });

    const form = formFor(po);
    syncPlanInput(form, po.dataset.lytesPlan);
    const idInput = form && form.querySelector('[name="id"]');
    const variant = (idInput && data.variants[idInput.value]) || data.variants[data.first];
    if (!variant) return;

    const autoPrice = (variant.plans && variant.plans[plan]) || variant.price;
    const set = (selector, text) => {
      const node = po.querySelector(selector);
      if (node) node.textContent = text;
    };
    set('[data-lytes-po-one]', money(variant.price, data.moneyFormat));
    set('[data-lytes-po-one-was]', variant.compare > variant.price ? money(variant.compare, data.moneyFormat) : '');
    set('[data-lytes-po-auto]', money(autoPrice, data.moneyFormat));
    const autoWas = Math.max(variant.price, variant.compare || 0);
    set('[data-lytes-po-auto-was]', autoWas > autoPrice ? money(autoWas, data.moneyFormat) : '');
    if (data.serves > 1) {
      set('[data-lytes-po-one-pps]', `${money(Math.round(variant.price / data.serves), data.moneyFormat)} / serve`);
      set('[data-lytes-po-auto-pps]', `${money(Math.round(autoPrice / data.serves), data.moneyFormat)} / serve`);
    }
  };

  /** One selling_plan input in the form, carrying the Auto Delivery plan (none for one-time) */
  const syncPlanInput = (form, plan) => {
    if (!form) return;
    form.querySelectorAll('input[name="selling_plan"]:not([data-lytes-plan])').forEach((input) => input.remove());
    let input = form.querySelector('input[data-lytes-plan]');
    if (!plan) {
      if (input) input.remove();
      return;
    }
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'selling_plan';
      input.dataset.lytesPlan = '';
      form.appendChild(input);
    }
    if (input.value !== plan) input.value = plan;
  };

  const removeSeal = (po) => {
    sectionOf(po)
      .querySelectorAll('.sealsubs-container, .sealsubs-target-element')
      .forEach((el) => {
        if (!el.contains(po)) el.remove();
      });
  };

  const refreshAll = () => {
    document.querySelectorAll('[data-lytes-po]').forEach((po) => {
      restore(po);
      removeSeal(po);
      update(po);
    });
  };

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || form.dataset.type !== 'add-to-cart-form') return;
      const po = sectionOf(form).querySelector('[data-lytes-po]');
      if (!po) return;
      update(po);
    },
    true
  );

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const freq = target && target.closest('[data-lytes-po-freqs] button');
    if (!freq) return;
    event.preventDefault();
    const po = freq.closest('[data-lytes-po]');
    freq.parentElement.querySelectorAll('button').forEach((button) => {
      button.setAttribute('aria-pressed', String(button === freq));
    });
    const auto = po.querySelector('input[data-lytes-po-mode="auto"]');
    if (auto) auto.checked = true;
    update(po);
  });

  // The whole option card selects its option, not just the radio and its label
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const opt = target && target.closest('[data-lytes-po] .lytes-po__opt');
    if (!opt || target.closest('button, input, label, a')) return;
    const radio = opt.querySelector('.lytes-po__radio');
    if (radio && !radio.checked) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });

  document.addEventListener('change', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const po = target && target.closest('[data-lytes-po]');
    if (po) update(po);
  });

  // Variant changes re-render the product section; Seal injects its widget after load.
  let pending = false;
  new MutationObserver((mutations) => {
    if (pending) return;
    const relevant = mutations.some((mutation) =>
      [...mutation.addedNodes].some(
        (node) =>
          node instanceof Element &&
          (node.matches('[data-lytes-po], .sealsubs-container, input[name="id"]') ||
            node.querySelector('[data-lytes-po], .sealsubs-container'))
      )
    );
    const idChanged = mutations.some(
      (mutation) => mutation.type === 'attributes' && mutation.target instanceof HTMLInputElement && mutation.target.name === 'id'
    );
    if (!relevant && !idChanged) return;
    pending = true;
    requestAnimationFrame(() => {
      pending = false;
      refreshAll();
    });
  }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['value'] });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refreshAll);
  } else {
    refreshAll();
  }
})();
